import React, { ReactNode } from 'react';
import {
  Modal as CModal,
  ModalProps as CModalProps,
  ModalContent,
  ModalOverlay,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FocusableElement } from '@chakra-ui/utils';

export interface ModalProps extends CModalProps {
  allowEscape?: boolean;
  isCentered?: boolean;
  useFullScreenMobile?: boolean;
  size?: string;
  scrollBehavior?: 'inside' | 'outside';
  children: ReactNode;
  initialFocusRef?: React.RefObject<FocusableElement>;
  isExpanded?: boolean;
  dataTestId?: string;
}

export const Modal = ({
  children,
  isOpen,
  onClose,
  isCentered = true,
  allowEscape = true,
  useFullScreenMobile = true,
  initialFocusRef,
  scrollBehavior,
  size = 'xl',
  isExpanded = false,
  dataTestId = 'modal',
  ...rest
}: ModalProps): JSX.Element => {
  // Responsive values for full screen modals on phones.
  const fullScreenMobileSize = useBreakpointValue({ base: 'full', md: size });
  const fullScreenMobileBorderRadius = useBreakpointValue({ base: 0, md: 'xl' });
  const modalMargin = useBreakpointValue({ base: useFullScreenMobile ? 0 : 16, md: 16 });

  const Modal: JSX.Element = (
    <CModal
      closeOnEsc={allowEscape}
      closeOnOverlayClick={allowEscape}
      autoFocus={!!initialFocusRef}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior={scrollBehavior}
      isCentered={isCentered}
      size={useFullScreenMobile || isExpanded ? fullScreenMobileSize : size}
      motionPreset="slideInBottom"
      data-testid={dataTestId}
      {...rest}
    >
      <ModalOverlay
        bg={isExpanded ? 'neutral.100' : 'blackAlpha.600'}
        style={{ transition: 'background-color 0.7s ease-in-out' }}
        className="Modal__Overlay"
        data-testid={`${dataTestId}-overlay`}
      />
      <ModalContent
        backgroundColor="background.overlay"
        data-testid={`${dataTestId}-content`}
        m={modalMargin}
        {...(!useFullScreenMobile && { mx: 5 })}
        borderRadius={isExpanded ? 'none' : useFullScreenMobile ? fullScreenMobileBorderRadius : 'xl'}
        minH={{ md: isExpanded ? '100%' : '100px', ...(useFullScreenMobile && { base: '-webkit-fill-available' }) }}
        minW={{ md: isExpanded ? '650px' : '300px' }}
        maxH={scrollBehavior === 'inside' ? '100%' : undefined}
        transition="min-height 0.7s ease-in-out, min-width 0.7s ease-in-out, border-radius 0.7s ease-in-out"
        className="Modal__Content"
      >
        {children}
      </ModalContent>
    </CModal>
  );

  return Modal;
};
